import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sheet, SheetContent } from '@mlm/ui';
import { Menu } from 'lucide-react';
import { creatorAvatarUrl, getCreatorMe, getTenantFlags, logout, tenantLogoUrl } from '../lib/api';
import { NotificationsBell } from './notifications-bell';
import { MessagesBell } from './messages-bell';
import { ImpersonationBanner } from './impersonation-banner';
import { useHouseMoneyHidden } from '../lib/use-house-money';
import { useAuthStore } from '../lib/auth-store';

type NavItem = { to: string; label: string; gated: boolean };
type NavGroup = { label: string; items: NavItem[] };

/**
 * The account's one-line standing, as a dot + words rather than a badge: the
 * rail is dark and a shadcn badge on it reads as a button.
 */
function statusLine(
  status: string | undefined,
  isHouse: boolean,
): { text: string; dot: string; tone: string } | null {
  if (isHouse) return { text: 'House account', dot: 'bg-lms-gold', tone: 'text-lms-gold/90' };
  switch (status) {
    case 'approved':
      return { text: 'Approved creator', dot: 'bg-[#6FBF87]', tone: 'text-[#9CC5A8]' };
    case 'pending':
      return { text: 'Application pending', dot: 'bg-lms-gold', tone: 'text-lms-gold/90' };
    case 'rejected':
      return { text: 'Application rejected', dot: 'bg-destructive', tone: 'text-white/70' };
    case 'suspended':
      return { text: 'Account suspended', dot: 'bg-destructive', tone: 'text-white/70' };
    default:
      return null;
  }
}

/**
 * Nav links, shared by the desktop rail and the mobile drawer. ONE definition:
 * two copies is how a nav entry ends up on one breakpoint and not the other.
 *
 * Grouped into Overview / Content / Earnings / Account so a rail of a dozen
 * items reads as four short lists. A group whose items are all conditioned away
 * (a house account has no coupons and no wallet) renders nothing at all rather
 * than leaving a heading over empty space.
 */
function NavList({
  groups,
  approved,
  onNavigate,
}: {
  groups: NavGroup[];
  approved: boolean;
  onNavigate?: () => void;
}) {
  return (
    <nav className="flex-1 overflow-y-auto px-3.5 py-1.5">
      {groups
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <div key={g.label}>
            <div className="mb-1.5 mt-3 px-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-white/40">
              {g.label}
            </div>
            <div className="space-y-0.5">
              {g.items.map((item) => {
                const disabled = item.gated && !approved;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      [
                        'flex items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-[13.5px] transition-colors',
                        disabled
                          ? 'pointer-events-none opacity-40'
                          : isActive
                            ? 'bg-lms-gold/15 font-bold text-[#F0C878]'
                            : 'text-white/[0.78] hover:bg-white/10',
                      ].join(' ')
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={`h-1.5 w-1.5 flex-none rounded-full ${
                            isActive ? 'bg-lms-gold' : 'bg-white/20'
                          }`}
                        />
                        {item.label}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
    </nav>
  );
}

/**
 * Creator Studio shell. Gated items are disabled until the account is APPROVED
 * (a pending creator can only see the dashboard status card + edit their
 * profile / requirements).
 *
 * The rail carries the LEARNING palette (deep green + gold), shared with the
 * member academy pages, and the tenant's own mark and name — Creator Studio is
 * a white-labelled surface, not a neutral admin tool.
 *
 * Responsive on the member SPA's pattern: the rail is `hidden md:flex`, and
 * below `md` a header row carries a hamburger that opens the same `NavList` in
 * a Sheet. The bells live in the rail's account card on desktop and in that
 * header row on mobile — never both at once, or a phone shows two unread
 * badges for one inbox.
 */
export function Layout() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { data } = useQuery({ queryKey: ['creator-me'], queryFn: getCreatorMe });
  const { data: flags } = useQuery({ queryKey: ['tenant-flags'], queryFn: getTenantFlags });
  const [mobileOpen, setMobileOpen] = useState(false);
  const status = data?.creator.status;
  const approved = status === 'approved';
  const isHouse = data?.creator.isHouse === true;
  // A HOUSE account's courses sell as house products, so no wallet ever
  // accrues — see the hook for why this is not simply `isHouse`.
  const hideMoney = useHouseMoneyHidden();
  const messaging = flags?.messagingEnabled === true;
  const tenantName = flags?.tenantName || '';
  const theme = flags?.theme ?? null;
  // The rail is dark, so prefer the DARK logo slot; each slot 404s on its own
  // when unset, so pick by the key rather than letting an <img> fail.
  const logoSrc = theme?.logoKeyDark
    ? `${tenantLogoUrl('dark')}?v=${encodeURIComponent(theme.logoKeyDark)}`
    : theme?.logoKey
      ? `${tenantLogoUrl('light')}?v=${encodeURIComponent(theme.logoKey)}`
      : (theme?.logoUrlDark ?? theme?.logoUrl ?? null);
  const creatorUserId = data?.creator.userId;
  const displayName = data?.creator.displayName ?? 'Creator';
  const standing = statusLine(status, isHouse);

  const navGroups: NavGroup[] = [
    { label: 'Overview', items: [{ to: '/', label: 'Dashboard', gated: false }] },
    {
      label: 'Content',
      items: [
        { to: '/courses', label: 'Courses', gated: true },
        { to: '/questions', label: 'Questions', gated: true },
        { to: '/grading', label: 'Grading', gated: true },
        { to: '/certificates', label: 'Certificates', gated: true },
        // Phase 10: only under the CREATOR subscription model. Under the tenant
        // model the instructor has no plan of their own to price, so the page
        // would be a dead end.
        ...(flags?.subscriptionModel === 'creator'
          ? [{ to: '/subscription-plan', label: 'Subscription plan', gated: true }]
          : []),
      ],
    },
    {
      label: 'Earnings',
      items: [
        ...(hideMoney
          ? []
          : ([
              { to: '/revenue', label: 'Revenue', gated: true },
              { to: '/wallet', label: 'Wallet', gated: true },
              { to: '/payouts', label: 'Payouts', gated: true },
            ] as NavItem[])),
        // A HOUSE creator has no coupons of its own: its courses sell through
        // the house checkout, which never resolves a creator-owned code.
        ...(isHouse ? [] : [{ to: '/coupons', label: 'Coupons', gated: true }]),
      ],
    },
    {
      label: 'Account',
      items: [
        ...(messaging ? [{ to: '/messages', label: 'Messages', gated: true }] : []),
        { to: '/profile', label: 'Profile', gated: false },
      ],
    },
  ];

  async function onLogout() {
    await logout();
    navigate('/login');
  }

  const bells = (
    <div className="flex items-center gap-1">
      {messaging && <MessagesBell />}
      <NotificationsBell />
    </div>
  );

  const brand = (
    <div className="flex items-center gap-2.5 px-[18px] pb-4 pt-5">
      {logoSrc ? (
        <img src={logoSrc} alt="" className="h-8 w-8 flex-none rounded-[9px] object-contain" />
      ) : (
        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-lms-gold font-lms-heading text-base font-extrabold text-lms-deep">
          {(tenantName || 'C').slice(0, 1).toUpperCase()}
        </span>
      )}
      <div className="min-w-0">
        <div className="font-lms-heading text-base font-bold tracking-[-0.01em] text-white">
          Creator Studio
        </div>
        {tenantName && <div className="truncate text-[11px] text-white/50">{tenantName}</div>}
      </div>
    </div>
  );

  const accountCard = (
    <div className="mx-3.5 mb-2.5 mt-1 flex items-center gap-2.5 rounded-xl bg-white/[0.07] px-3 py-2.5">
      {creatorUserId && data?.creator.avatarKey ? (
        <img
          src={creatorAvatarUrl(creatorUserId)}
          alt=""
          className="h-[34px] w-[34px] flex-none rounded-full object-cover"
        />
      ) : (
        <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full bg-white/15 text-[13px] font-bold text-white">
          {displayName.slice(0, 1).toUpperCase()}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-bold text-white">{displayName}</div>
        {standing && (
          <div className={`flex items-center gap-1.5 text-[11px] ${standing.tone}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${standing.dot}`} />
            {standing.text}
          </div>
        )}
      </div>
      <div className="flex-none">{bells}</div>
    </div>
  );

  const accountFooter = (
    <div className="border-t border-white/10 px-[18px] py-3.5 text-xs text-white/50">
      <div className="mb-2 truncate">{user?.email}</div>
      <div className="flex items-center gap-3">
        {/* Changing your own password is never gated on approval status, so it
            lives here rather than in the (gated) nav above. */}
        <button
          type="button"
          onClick={() => {
            setMobileOpen(false);
            navigate('/change-password');
          }}
          className="text-[12.5px] font-semibold text-white/75 transition-colors hover:text-lms-gold"
        >
          Change password
        </button>
        <span className="h-3 w-px bg-white/20" />
        <button
          type="button"
          onClick={onLogout}
          className="text-[12.5px] font-semibold text-white/75 transition-colors hover:text-lms-gold"
        >
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col bg-lms-paper">
      <ImpersonationBanner />
      {/* Mobile header — the only place the hamburger and the bells live below md. */}
      <header className="flex items-center gap-2 border-b border-lms-hairline bg-white px-4 py-3 md:hidden">
        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
          className="flex h-9 w-9 flex-none items-center justify-center rounded-md text-lms-muted hover:bg-lms-light"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1 truncate font-semibold text-lms-ink">{displayName}</div>
        {bells}
      </header>
      {/* The shell is a sidebar/main ROW; the banner has to sit above it, so
          the row moved into its own flex child rather than stretching. */}
      <div className="flex flex-1">
        <aside className="hidden w-[248px] shrink-0 flex-col bg-lms-deep text-white/85 md:flex">
          {brand}
          {accountCard}
          <NavList groups={navGroups} approved={approved} />
          {accountFooter}
        </aside>
        {/* `min-w-0` is load-bearing on a phone: a flex item defaults to
            min-width:auto, so one unshrinkable child (a file input, a long R2
            key, a wide table) widened the whole document past the viewport and
            left blank gutter beside every shorter block. The admin and member
            shells have carried this since they were written. */}
        <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-6 md:px-9 md:py-8">
          <Outlet />
        </main>
      </div>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="flex w-72 flex-col overflow-y-auto border-0 bg-lms-deep p-0 text-white/85"
        >
          {brand}
          {accountCard}
          <NavList groups={navGroups} approved={approved} onNavigate={() => setMobileOpen(false)} />
          {accountFooter}
        </SheetContent>
      </Sheet>
    </div>
  );
}
